import React, { useContext, useEffect } from 'react';
import ZillowMapContext from '@zillow/zillow-map-control/dist/ZillowMapContext';

export type GeoJson = GeoJSON.FeatureCollection | GeoJSON.Feature | GeoJSON.Geometry;

export function GeoJsonLayer({
  data,
  style,
  onFeatureClick,
}: {
  data: GeoJson;
  style?: google.maps.Data.StylingFunction | google.maps.Data.StyleOptions;
  onFeatureClick?: (feature: google.maps.Data.Feature, latLng: google.maps.LatLng) => void;
}) {
  const { gmap } = useContext<{ gmap: google.maps.Map }>(ZillowMapContext);

  useEffect(() => {
    if (!gmap || !window.google?.maps?.Data) return;

    // Clear any previously added Data features
    gmap.data.forEach((f) => gmap.data.remove(f));

    // Add GeoJSON (Feature, FeatureCollection, or Geometry)
    try {
      if (data) {
        // addGeoJson only takes Feature/FeatureCollection; Geometry needs wrapping
        if (data.type === 'FeatureCollection' || data.type === 'Feature') {
          gmap.data.addGeoJson(data);
        } else {
          const fc: GeoJSON.FeatureCollection = {
            type: 'FeatureCollection',
            features: [{ type: 'Feature', properties: {}, geometry: data }],
          };
          gmap.data.addGeoJson(fc);
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to add GeoJSON', e);
    }

    // Style
    if (style) {
      gmap.data.setStyle(style);
    } else {
      // Default style: polygons + numbered point circles
      gmap.data.setStyle((feature) => {
        const geom = feature.getGeometry();
        const type = geom && geom.getType();

        // Style POINTS as numbered circles
        if (type === 'Point') {
          const label = String(feature.getProperty('label') ?? '');

          // Simple SVG circle with centered text
          const svg = `
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20">
            <rect x="3" y="5" width="13" height="10" rx="5" ry="4" fill="#891EFA" stroke-width="1" />
              <text x="50%" y="50%" text-anchor="middle" dy=".35em"
                    font-family="Arial" font-size="7" fill="white">${label}</text>
            </svg>
          `;
          const url = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);

          return /** @type {google.maps.Data.StyleOptions} */ {
            icon: {
              url,
              scaledSize: new google.maps.Size(40, 40),
            },
            zIndex: 1000,
          };
        }

        // Style POLYGONS (and other non-point geometries)
        return {
          fillColor: 'transparent',
          fillOpacity: 0.2,
          strokeColor: '#6000B6',
          strokeWeight: 4,
        };
      });
    }

    // Click handler
    let listener: google.maps.MapsEventListener | undefined;
    if (onFeatureClick) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      listener = gmap.data.addListener('click', (e: any) => {
        onFeatureClick(e.feature, e.latLng);
      });
    }

    return () => {
      if (listener) listener.remove();
      gmap.data.forEach((f) => gmap.data.remove(f));
    };
  }, [gmap, data, style, onFeatureClick]);

  return null;
}
